// SEO-12 · /resources/school-attendance-that-is-evidence
//
// EDITORIAL GROUNDING (see docs/marketing/RESOURCES_EDITORIAL_PROCESS.md):
//   • Period-wise attendance: `attendance.period_index` with partial-unique day/period registers,
//     the scheduled low-attendance scan, and the monthly parent digest (CLAUDE.md P1 parity block).
//   • Marking is section-scoped for a classroom persona — `core/teacher_scope.assert_section_access`
//     wired into `routers/attendance.py` (roster/mark/monthgrid).
//   • Biometric/RFID capture flows into the same attendance register (`biometric` module,
//     Enterprise-tier per the 2026-07-13 restructure); it is a capture method, not a second ledger.
//   • Absence alerts + parent notification ride the ONE messaging funnel with consent/opt-out and
//     quiet-mode rules (registry I2 wording).
//   • Attendance thresholds are board configuration, resolved through the 3-tier resolver (I-03).
// NO attendance-improvement statistics, no claimed outcome, no truancy-detection claim.

const article = {
  slug: "school-attendance-that-is-evidence",
  title: "Attendance that produces evidence, not a number",
  seoTitle: "School Attendance as Evidence | Vidyapeeth360",
  description:
    "Daily attendance answers little. A school needs a register that survives a dispute — per period, scoped to the teacher, with a real absent state.",
  topic: "Academics",
  topicKey: "academics",
  readingMinutes: 5,
  published: "2026-08-18",
  updated: "2026-08-18",
  standfirst:
    "Every school takes attendance and very few can answer the question it is actually for: on the afternoon a child was not where they should have been, who marked what, and when did somebody notice?",
  moduleLinks: ["student-attendance", "attendance-analytics", "period-attendance"],
  conversion: {
    to: "/pricing",
    label: "See plans and the full cost schedule",
    note: "Attendance is part of the day-one core in every plan; analytics depth and device capture follow the plan.",
  },
  sections: [
    {
      id: "daily-is-not-enough",
      heading: "One mark a day answers the wrong question",
      blocks: [
        {
          t: "p",
          text: "A single morning register tells you a child arrived. It does not tell you whether they were in the third period, which is the case that matters — the student who signs in and leaves, the class that was never taken because a teacher was absent, the afternoon nobody can reconstruct.",
        },
        {
          t: "p",
          text: "Period-wise attendance sounds like more work and mostly is not, because the marking is already happening in each classroom. What changes is that the register can answer a specific question about a specific hour instead of a general one about a day.",
        },
      ],
    },
    {
      id: "states",
      heading: "Absent is a state; blank is not",
      blocks: [
        {
          t: "p",
          text: "The most common defect in an attendance register is treating an unmarked student as present. It is convenient, it looks tidy, and it means a period nobody marked is indistinguishable from a period where everybody attended.",
        },
        {
          t: "ul",
          items: [
            "**Present, absent and unmarked are three different facts.** Only the first two are evidence; the third is a task for somebody.",
            "**Approved leave is not absence.** Merging them destroys both the attendance percentage and the leave record, and produces the conversation where a parent points out that the school approved it.",
            "**Late is worth its own state** if you act on it. If you do not act on it, do not collect it — a field nobody uses degrades the ones you do.",
            "**A correction is an edit somebody made**, with a name and a time attached. Attendance is the most-corrected register in a school and the least-audited.",
          ],
        },
      ],
    },
    {
      id: "who-marks",
      heading: "Who may mark, and for whom",
      blocks: [
        {
          t: "p",
          text: "Attendance is the surface where over-broad permissions do the most quiet damage. If any teacher can open any section's register, then a mark can be entered by somebody who was not in the room, and the register loses the one property that made it evidence: that the person who recorded it was there.",
        },
        {
          t: "p",
          text: "Scope marking to the sections a teacher is actually assigned — derived from the timetable and the teaching assignments rather than a hand-maintained list — and let the class teacher's homeroom period be an explicit assignment rather than a special case. A head of section or principal keeps the wider view; a subject teacher does not need it.",
        },
        {
          t: "note",
          title: "Devices are a capture method, not a second register",
          text: "Biometric or card capture at a gate is useful and should write into the same attendance register the teacher marks. What it must not become is a parallel record that disagrees with the classroom, leaving the office to decide which one to believe on the day it matters.",
        },
      ],
    },
    {
      id: "acting-on-it",
      heading: "Attendance that nobody reads is a filing exercise",
      blocks: [
        {
          t: "p",
          text: "The register earns its cost only when something happens because of it, and there are exactly three things worth wiring.",
        },
        {
          t: "ol",
          items: [
            "**A same-day absence notice to the family** — the only alert that can still change the outcome, and the one that must never fire on a blank register or a marked-late child.",
            "**A periodic low-attendance scan** against your board's own threshold, surfacing the students approaching it while there is still term left to fix it.",
            "**A monthly summary to the parent**, which converts attendance from a surprise at the end of the year into a shared fact all year — so a figure a family disputes is raised in October rather than at report-card time.",
          ],
        },
        {
          t: "p",
          text: "All three are outbound messages to families, so they inherit the school's consent, opt-out and quiet-period rules. An alert engine that ignores those is a complaint engine.",
        },
      ],
    },
    {
      id: "in-the-product",
      heading: "How this works in Vidyapeeth360",
      blocks: [
        {
          t: "p",
          text: "Attendance is a register per class-section per day, and optionally per period, with each period's register distinct so an afternoon can be answered separately from a morning. Present and absent are recorded states; an unmarked register stays visibly unmarked rather than defaulting to present.",
        },
        {
          t: "p",
          text: "A classroom teacher can open the roster, mark it and view the month grid only for the sections they are assigned — derived from the timetable and explicit teaching assignments, including the homeroom class a class teacher marks without a timetabled period. Broader roles keep the school-wide view.",
        },
        {
          t: "p",
          text: "Where a school uses biometric or card capture, those events land in the same attendance register rather than a parallel one. A scheduled scan surfaces students below the threshold resolved from the school's board configuration, a same-day absence alert can notify guardians, and a monthly digest summarises the month to the family — all three through the school's one messaging path, so consent, opt-out and a pre-go-live quiet period are honoured without each feature reimplementing them.",
        },
        {
          t: "p",
          text: "The reporting is the point: [student attendance](/modules/student-attendance) records it, [period attendance](/modules/period-attendance) makes the hour answerable, and [attendance analytics](/modules/attendance-analytics) shows the pattern before it becomes a year-end problem.",
        },
      ],
    },
  ],
  sources: [],
};

export default article;
